import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import { ErrorPage404, ErrorPage500 } from '@pages/ErrorPage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Select, Typography } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './MondayInstall.less';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { isAxiosError } from 'axios';

const { Title, Paragraph } = Typography;

const consentKey = 'monday-data-retention-consent';

interface MondayOAuthInstallProps {
  loader: ReactElement;
}

export default function MondayOAuthInstall({
  loader
}: MondayOAuthInstallProps) {
  const [searchParams] = useSearchParams();
  const mondayClient = searchParams.get('client');
  const mondayToken = searchParams.get('token');
  const mutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const {
        data: { url, handle }
      } = await apiClient.monday.mondayControllerInstall({
        client: mondayClient ?? undefined,
        token: nonNull(mondayToken),
        organizationId
      });
      sessionStorage.setItem('monday-oauth-handle', handle);
      window.location.href = url;
    }
  });
  const { mutate } = mutation;

  const defaultConsent = useMemo(() => {
    return localStorage.getItem(consentKey) === 'true';
  }, []);
  const [consentGiven, setConsentGiven] = useState(defaultConsent);
  const [step, setStep] = useState<'consent' | 'organization' | 'loader'>(
    'consent'
  );
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const {
        data: { list }
      } =
        await apiClient.organization.organizationControllerListUserOrganizations();
      return list;
    }
  });
  const organizations = useMemo(
    () => organizationsQuery.data || [],
    [organizationsQuery.data]
  );

  const defaultOrgId =
    organizations.length === 1 ? organizations[0].id : undefined;
  const isError = organizationsQuery.isError || mutation.isError;
  const proceedAutomatically = defaultConsent && !!defaultOrgId;

  useEffect(() => {
    if (proceedAutomatically) {
      mutate(defaultOrgId);
    }
  }, [proceedAutomatically]);

  if (!mondayToken) {
    return <ErrorPage404 />;
  }

  if (isError) {
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_permission_denied'
    ) {
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <div className="monday-install__lock-icon">
              <LockSvg />
            </div>
            <Title level={2} className="monday-install__title">
              You don&apos;t have permissions to install integrations
            </Title>
            <Title level={5} className="monday-install__description">
              Contact your organization admin to install the monday.com
              integration
            </Title>
          </div>
        </div>
      );
    }

    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_already_has_monday_integration'
    ) {
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <Title level={2} className="monday-install__title">
              This organization already has a monday.com integration installed
            </Title>
            <Title level={5} className="monday-install__description">
              Contact{' '}
              <a href="mailto:support@streamwork.com">support@streamwork.com</a>{' '}
              if you believe this is a mistake
            </Title>
          </div>
        </div>
      );
    }

    return <ErrorPage500 />;
  }

  if (organizationsQuery.isLoading || proceedAutomatically) {
    return loader;
  }

  if (step === 'consent') {
    return (
      <div className="monday-install">
        <div className="monday-install__content">
          <Title level={2} className="monday-install__title">
            Data Retention Consent
          </Title>
          <Paragraph className="monday-install__description">
            StreamWork is a standalone platform, and the monday.com app is just
            one way to stay connected. To ensure you don&apos;t lose access to
            your projects and settings if the monday.com app is ever removed, we
            need your permission to keep your account data active.
          </Paragraph>
          <Paragraph className="monday-install__description">
            By checking this box, you agree to let StreamWork retain your data
            so you can keep using your account independently at any time.
            You&apos;re always in control — you can delete your data whenever
            you like by closing your StreamWork account or by reaching out to{' '}
            <a href="mailto:support@streamwork.com">support@streamwork.com</a>.
          </Paragraph>
          <Checkbox
            className="monday-install__description"
            value={consentGiven}
            onChange={(e) => {
              setConsentGiven(e.target.checked);
              localStorage.setItem(
                consentKey,
                e.target.checked ? 'true' : 'false'
              );
            }}
          >
            <b>
              I agree to StreamWork retaining my data for continued platform use
            </b>
          </Checkbox>
          <Button
            className="monday-install__button"
            type="primary"
            onClick={() => {
              if (defaultOrgId) mutate(defaultOrgId);
              else setStep('organization');
            }}
            disabled={!consentGiven}
            loading={mutation.isPending || mutation.isSuccess}
          >
            Next
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="monday-install">
      <div className="monday-install__content">
        <Title level={2} className="monday-install__title">
          Select Organization
        </Title>
        <Title level={5} className="monday-install__description">
          Choose an organization to connect with monday.com account
        </Title>
        <Select
          className="monday-install__select"
          size="large"
          loading={mutation.isPending}
          placeholder="Select an organization..."
          onChange={setSelectedOrganizationId}
          options={organizations.map((org) => ({
            label: org.name,
            value: org.id
          }))}
        />
        <Button
          className="monday-install__button"
          type="primary"
          onClick={() => mutate(selectedOrganizationId)}
          disabled={!selectedOrganizationId}
          loading={mutation.isPending || mutation.isSuccess}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
